import React, { useEffect, useState } from 'react';
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Circle,
  Clock,
  RefreshCw,
  Search
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import LoggedInUserWrapper from '../core/LoggedInUserWrapper';
import { useStudentActivityAssignments } from '../../queries';
import { getAssignmentEndUrl, getAssignmentStartUrl } from '../../utils';
import { useActivityGroups } from './hooks/useActivityGroups';
import ActivityTabBar from './ActivityTabBar';
import ActivityTable from './ActivityTable';
import ActivityPdfModal from './ActivityPdfModal';
import ActivityActionButton from './ActivityActionButton';
import { Skeleton } from '../ui/skeleton';

function openStudentActivity(activity, navigate, onOpenPdf) {
  if (activity?.pdfUrl) {
    onOpenPdf(activity);
    return;
  }
  if (activity.canView) {
    if (activity.revealSolutions === false) return;
    if (!activity?.studentAssignmentId) return;
    navigate(
      getAssignmentEndUrl({
        studentAssignmentId: activity.studentAssignmentId,
        showResult: true
      })
    );
    return;
  }
  if (activity.canResume) {
    if (activity.isDueDatePassed) return;
    if (activity.assignmentId) {
      navigate(getAssignmentStartUrl({ assignmentId: activity.assignmentId }));
      return;
    }
    if (activity.studentAssignmentId) {
      navigate(`/assignment/${activity.studentAssignmentId}`);
    }
  }
}

function StatusIcon({ status }) {
  if (status === 'completed') {
    return <CheckCircle2 className="h-4 w-4 flex-shrink-0" style={{ color: '#00C950' }} />;
  }
  if (status === 'in-progress') {
    return <Clock className="h-4 w-4 flex-shrink-0 text-amber-400" />;
  }
  return <Circle className="h-4 w-4 flex-shrink-0 text-gray-300" />;
}

function MobileActivityCard({ activity, onPress }) {
  const TypeIcon = activity.TypeIcon;
  const clickable = Boolean(activity.pdfUrl || activity.canView || activity.canResume);

  return (
    <div className="w-full rounded-xl border border-white/50 bg-white/70 text-left shadow-sm">
      <div className="flex items-start gap-3 p-3">
        <div
          className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full"
          style={{ backgroundColor: activity.colors.iconBg }}
        >
          {TypeIcon ? (
            <TypeIcon className="h-4 w-4" style={{ color: activity.colors.iconColor }} strokeWidth={1.8} />
          ) : null}
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-0.5 flex items-center gap-2">
            <span className="truncate text-sm font-semibold text-gray-900">{activity.title}</span>
            {activity.displayDate ? (
              <span className="whitespace-nowrap text-xs text-gray-400">{activity.displayDate}</span>
            ) : null}
          </div>
          <p className="mb-1.5 text-xs text-gray-500">{activity.typeName}</p>
          <div className="mb-2 flex items-center gap-2">
            {activity.score ? (
              <span className="rounded-md bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                Score: {activity.score}
              </span>
            ) : null}
            <span className="text-xs text-gray-400">{activity.details}</span>
          </div>
          {clickable ? <ActivityActionButton activity={activity} onView={onPress} /> : null}
        </div>
        <div className="mt-1 flex-shrink-0">
          <StatusIcon status={activity.status} />
        </div>
      </div>
    </div>
  );
}

export function ActivityPage() {
  const navigate = useNavigate();
  const [desktopTab, setDesktopTab] = useState('all');
  const [mobileFilter, setMobileFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [pdfActivity, setPdfActivity] = useState(null);

  const {
    data: assignments,
    isLoading,
    isFetching,
    isError,
    error,
    refetch
  } = useStudentActivityAssignments();

  const { desktopRows, subjectGroups, desktopTabs, mobileTabs } = useActivityGroups({
    assignments,
    selectedTab: desktopTab,
    mobileFilter,
    searchQuery
  });

  useEffect(() => {
    setExpandedIds(new Set(subjectGroups.map((group) => group.id)));
  }, [searchQuery, subjectGroups]);

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleRow = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllRows = () => {
    const allSelected =
      desktopRows.length > 0 && desktopRows.every((activity) => selectedIds.has(activity.id));
    setSelectedIds(allSelected ? new Set() : new Set(desktopRows.map((activity) => activity.id)));
  };

  const handleActivityPress = (activity) =>
    openStudentActivity(activity, navigate, setPdfActivity);

  return (
    <LoggedInUserWrapper>
      <div className="flex min-h-0 flex-1 flex-col bg-white">
        <div
          className="flex min-h-0 flex-1 flex-col overflow-hidden md:hidden"
          style={{
            background:
              'linear-gradient(115deg, #f9fafb 0%, rgba(239,246,255,0.4) 50%, rgba(238,242,255,0.4) 100%)'
          }}
        >
          <div className="sticky top-0 z-20 flex-shrink-0 border-b border-slate-200/60 bg-[#f9fafb]/95 px-4 pt-6 pb-4 backdrop-blur-md">
            <h1 className="mb-5 text-center text-lg font-bold text-gray-900">Your Activity</h1>

            <div className="mb-3 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search assessments..."
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  className="w-full rounded-xl border border-gray-200 bg-white/80 py-2 pr-3 pl-9 text-sm outline-none transition-all focus:border-primary/40 focus:ring-2 focus:ring-primary/20"
                />
              </div>
              <button
                type="button"
                onClick={() => refetch()}
                className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-primary shadow-sm"
                aria-label="Refresh activities"
              >
                <RefreshCw
                  className={`h-4 w-4 text-white ${isFetching ? 'animate-spin' : ''}`}
                  strokeWidth={2}
                />
              </button>
            </div>

            <ActivityTabBar
              tabs={mobileTabs}
              selectedTab={mobileFilter}
              onTabChange={setMobileFilter}
              variant="pills"
            />
          </div>

          <div className="flex-1 overflow-y-auto px-4 pb-8 pt-4">
            {isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="space-y-2 rounded-xl bg-white/70 p-3">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-16 w-full rounded-xl" />
                  </div>
                ))}
              </div>
            ) : isError ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <Search className="mb-3 h-10 w-10 text-gray-300" />
                <p className="text-sm font-medium text-gray-500">Could not load activities</p>
                <p className="mt-1 max-w-xs text-xs text-gray-400">
                  {error?.response?.data?.message || error?.message || 'Please try again.'}
                </p>
              </div>
            ) : subjectGroups.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <Search className="mb-3 h-10 w-10 text-gray-300" />
                <p className="text-sm font-medium text-gray-500">No activities found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {subjectGroups.map((group) => {
                  const SubjectIcon = group.colors.Icon;
                  const isExpanded = expandedIds.has(group.id);
                  return (
                    <div
                      key={group.id}
                      className="overflow-hidden rounded-xl"
                      style={{ backgroundColor: group.colors.bgColor }}
                    >
                      <button
                        type="button"
                        onClick={() => toggleExpanded(group.id)}
                        className="flex w-full items-center gap-2 px-3 py-3 text-left"
                      >
                        {SubjectIcon ? (
                          <SubjectIcon
                            className="h-3.5 w-3.5 flex-shrink-0"
                            style={{ color: group.colors.textColor }}
                            strokeWidth={1.8}
                          />
                        ) : null}
                        <div className="min-w-0 flex-1">
                          <p
                            className="text-sm font-semibold"
                            style={{ color: group.colors.textColor }}
                          >
                            {group.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {group.activities.length}{' '}
                            {group.activities.length === 1 ? 'activity' : 'activities'}
                          </p>
                        </div>
                        {isExpanded ? (
                          <ChevronUp
                            className="h-4 w-4 flex-shrink-0"
                            style={{ color: group.colors.textColor }}
                          />
                        ) : (
                          <ChevronDown
                            className="h-4 w-4 flex-shrink-0"
                            style={{ color: group.colors.textColor }}
                          />
                        )}
                      </button>
                      {isExpanded ? (
                        <div className="space-y-2 px-3 pb-3">
                          {group.activities.map((activity) => (
                            <MobileActivityCard
                              key={activity.id}
                              activity={activity}
                              onPress={handleActivityPress}
                            />
                          ))}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="hidden min-h-0 flex-1 flex-col bg-white md:flex">
          <div className="flex flex-shrink-0 items-center justify-between px-8 pt-8 pb-5">
            <h1 className="text-2xl font-bold text-gray-900">Your Activity</h1>
            <div className="relative w-72">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by assessment name"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-white py-2 pr-3 pl-9 text-sm outline-none transition-all focus:border-primary/40 focus:ring-2 focus:ring-primary/20"
              />
            </div>
          </div>

          <div className="flex-shrink-0 px-8">
            <ActivityTabBar
              tabs={desktopTabs}
              selectedTab={desktopTab}
              onTabChange={setDesktopTab}
            />
          </div>

          <div className="flex-1 overflow-x-auto overflow-y-auto px-8">
            <ActivityTable
              activities={desktopRows}
              isLoading={isLoading}
              errorMessage={
                isError
                  ? error?.response?.data?.message || error?.message || 'Could not load activities'
                  : null
              }
              selectedIds={selectedIds}
              onToggleRow={toggleRow}
              onToggleAll={toggleAllRows}
              onView={handleActivityPress}
            />
          </div>
        </div>
      </div>
      <ActivityPdfModal
        activity={pdfActivity}
        open={Boolean(pdfActivity)}
        onClose={() => setPdfActivity(null)}
      />
    </LoggedInUserWrapper>
  );
}

export default ActivityPage;
